import { useColumnPreferencesContext } from "@context-providers/table/column-preferences-provider";
import { updatePreferencesWithOrder } from "@context-providers/table/column-preferences-utils";
import { FilterMenu, Option } from "@flat-ui";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import {
  setIsFilterMenuOpen,
  setOriginalColumnPreferences,
} from "@store/table/table-slice";

interface Props<K extends string> {
  /** Mapping of column field names to human-readable labels */
  labelsMap: Record<K, string>;

  /** Optional list of columns that should be disabled (always visible, cannot be toggled) */
  disabledColumns?: K[];
}

type ColumnOption<K extends string> = Pick<Option, "label" | "isDisabled"> & {
  key: K;
  value: K;
};

export function FaroTableColumnVisibilityMenu<K extends string>({
  labelsMap,
  disabledColumns = [],
}: Props<K>): JSX.Element {
  const dispatch = useAppDispatch();
  const {
    updateColumnPreferences,
    updateColumnPreferencesLocally,
    columnPreferences,
    dataColumns,
  } = useColumnPreferencesContext();

  const { isOpen, anchorPosition, originalColumnPreferences } = useAppSelector(
    (state) => state.table.filterMenu
  );

  function handleCloseMenu(): void {
    if (!columnPreferences) {
      dispatch(setIsFilterMenuOpen(false));
      dispatch(setOriginalColumnPreferences(undefined));
      return;
    }

    // Only save if visibility changed
    const hasChanged = Object.keys(columnPreferences).some(
      (key) =>
        columnPreferences[key]?.visible !==
        originalColumnPreferences?.[key]?.visible
    );

    if (hasChanged) {
      void updateColumnPreferences(columnPreferences);
    }
    dispatch(setIsFilterMenuOpen(false));
    dispatch(setOriginalColumnPreferences(undefined));
  }

  function handleFilterChange(newSelected: ColumnOption<K>[]): void {
    if (!columnPreferences) {
      return;
    }

    // Build new visibility state based on selected options
    const selectedKeys = new Set(newSelected.map((opt) => opt.key));

    // Update all preferences with new visibility
    const updatedPreferences = updatePreferencesWithOrder(
      columnPreferences,
      selectedKeys
    );

    updateColumnPreferencesLocally(updatedPreferences);
  }

  const disabledColumnsSet = new Set(disabledColumns);

  // Convert availableColumns to FilterMenu format
  const options: ColumnOption<K>[] = dataColumns
    .filter((field): field is K => field in labelsMap)
    .map((field) => ({
      key: field,
      value: field,
      label: labelsMap[field],
      isDisabled: disabledColumnsSet.has(field),
    }));

  // Convert columnPreferences to selectedOptions
  const selectedOptions = options.filter(
    (option) => columnPreferences?.[option.key]?.visible === true
  );

  return (
    <FilterMenu
      open={isOpen}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
      headingLabel="Select Columns"
      options={options}
      selectedOptions={selectedOptions}
      onFilterChange={handleFilterChange}
      onFilterMenuClose={handleCloseMenu}
      showSearch={false}
      showClearAll={false}
    />
  );
}
